import { useCallback, useEffect, useReducer } from 'react'

import { Office } from '~/models/types'

import { MapContext, MapDispatchContext } from './context'
import { mapReducer } from './reducer'
import {
  drawerContent,
  DrawerContent,
  drawerPosition,
  DrawerPosition,
} from './types'

type MapProviderProps = {
  children: React.ReactNode
  initialSelectedOffice: Office | null
}

export const MapProvider = ({
  children,
  initialSelectedOffice,
}: MapProviderProps) => {
  const [state, dispatch] = useReducer(mapReducer, {
    isOpenDrawer: false,
    isOpenInfoWindow: false,
    clickedPin: initialSelectedOffice ?? null,
    currentDrawerPosition: drawerPosition.bottom,
    currentDrawerContent: initialSelectedOffice
      ? drawerContent.detail
      : drawerContent.list,
    prevDrawerContent: drawerContent.list,
  })

  useEffect(() => {
    if (window.innerWidth >= 768) {
      dispatch({ type: 'OPEN' })
    }
  }, [])

  const openDrawer = () => {
    dispatch({ type: 'OPEN' })
  }

  const closeDrawer = () => {
    dispatch({ type: 'CLOSE' })
  }

  const openInfoWindow = (office: Office) => {
    dispatch({ type: 'OPEN_INFO_WINDOW', payload: office })
  }

  const closeInfoWindow = () => {
    dispatch({ type: 'CLOSE_INFO_WINDOW' })
  }

  const openSearchForm = () => {
    dispatch({ type: 'ON_OPEN_SEARCH_FORM' })
  }

  const closeSearchForm = () => {
    dispatch({ type: 'ON_CLOSE_SEARCH_FORM' })
  }

  // NOTE: map-pageのuseEffectで呼ばれるためuseCallbackでラップする
  const setDrawerPosition = useCallback((drawerPosition: DrawerPosition) => {
    dispatch({ type: 'SET_DRAWER_POSITION', payload: drawerPosition })
  }, [])

  const setDrawerContent = (drawerContent: DrawerContent) => {
    dispatch({ type: 'SET_DRAWER_CONTENT', payload: drawerContent })
  }

  const onClickOfficeCard = (office: Office) => {
    dispatch({ type: 'ON_CLICK_OFFICE_CARD', payload: office })
  }

  const onClickGoToList = () => {
    dispatch({ type: 'ON_CLICK_GO_TO_LIST' })
  }

  const dispatchContextValue = {
    openDrawer,
    closeDrawer,
    setDrawerPosition,
    setDrawerContent,
    onClickOfficeCard,
    openInfoWindow,
    closeInfoWindow,
    openSearchForm,
    closeSearchForm,
    onClickGoToList,
  }

  return (
    <MapContext.Provider value={state}>
      <MapDispatchContext.Provider value={dispatchContextValue}>
        {children}
      </MapDispatchContext.Provider>
    </MapContext.Provider>
  )
}
